import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/common/Card";
import * as utils from "../utils/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/common/Select";

import {
  Shield,
  FileText,
  Key,
  CheckCircle,
  AlertTriangle,
  Info,
  Building,
  CreditCard,
  ArrowLeft,
} from "lucide-react";

import { useAuth } from "../lib/AuthContext";
import * as Tabs from "@radix-ui/react-tabs";
import { supabase } from "../lib/supabase";
import { formatDateDMY } from "../lib/ScheduleUtils";

import { useState, useEffect } from "react";
import Alert from "../components/common/Alert";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Textarea } from "../components/common/Textarea";
import { Input } from "../components/common/Input";
import { Label } from "../components/common/Label";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function AfipConfiguration() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    cuit: "",
    puntoVenta: "1",
    ambiente: "testing",
    razonSocial: "",
    domicilioComercial: "",
    condicionIva: "Responsable Inscripto",
    certificadoFile: null,
    clavePrivadaFile: null,
    fechaVencimiento: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (user?.id) {
      loadAfipConfig();
    }
  }, [user]);

  const loadAfipConfig = async () => {
    try {
      const { data, error } = await supabase
        .from("afip_configurations")
        .select("*")
        .eq("doctor_id", user.id)
        .single();

      if (error && error.code !== "PGRST116") {
        console.error("Error loading AFIP config:", error);
        return;
      }

      if (data) {
        setConfig(data);
        setFormData({
          cuit: data.cuit || "",
          puntoVenta: data.punto_venta?.toString() || "1",
          ambiente: data.ambiente || "testing",
          razonSocial: data.razon_social || "",
          domicilioComercial: data.domicilio_comercial || "",
          condicionIva: data.condicion_iva || "Responsable Inscripto",
          certificadoFile: null,
          clavePrivadaFile: null,
          fechaVencimiento: data.fecha_vencimiento_certificado || "",
        });
      }
    } catch (error) {
      console.error("Error loading AFIP config:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      const validExtensions =
        field === "certificadoFile" ? [".crt", ".pem"] : [".key", ".pem"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setErrors((prev) => ({
          ...prev,
          [field]: t("app.afip.fileExtension", "Archivo debe tener extensión {{extensions}}", {
            extensions: validExtensions.join(
              t("app.afip.extensionOr", " o ")
            ),
          }),
        }));
        return;
      }

      if (file.size > 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [field]: t("app.afip.fileTooLarge", "El archivo no puede superar 1MB"),
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, [field]: file }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cuit || formData.cuit.length !== 11) {
      newErrors.cuit = t("app.afip.cuitInvalid", "CUIT debe tener 11 dígitos");
    }

    if (!formData.razonSocial.trim()) {
      newErrors.razonSocial = t(
        "app.afip.businessNameRequired",
        "Razón social es requerida"
      );
    }

    if (!formData.domicilioComercial.trim()) {
      newErrors.domicilioComercial = t(
        "app.afip.addressRequired",
        "Domicilio comercial es requerido"
      );
    }

    if (!config && !formData.certificadoFile) {
      newErrors.certificadoFile = t(
        "app.afip.certificateRequired",
        "Certificado es requerido"
      );
    }

    if (!config && !formData.clavePrivadaFile) {
      newErrors.clavePrivadaFile = t(
        "app.afip.privateKeyRequired",
        "Clave privada es requerida"
      );
    }

    if (!formData.fechaVencimiento) {
      newErrors.fechaVencimiento = t(
        "app.afip.expiryRequired",
        "Fecha de vencimiento es requerida"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setSuccess("");

    try {
      let certificadoContent = null;
      let clavePrivadaContent = null;

      if (formData.certificadoFile) {
        certificadoContent = await readFileAsText(formData.certificadoFile);
      }

      if (formData.clavePrivadaFile) {
        clavePrivadaContent = await readFileAsText(formData.clavePrivadaFile);
      }

      const configData = {
        doctor_id: user.id,
        cuit: formData.cuit,
        punto_venta: Number.parseInt(formData.puntoVenta),
        ambiente: formData.ambiente,
        razon_social: formData.razonSocial,
        domicilio_comercial: formData.domicilioComercial,
        condicion_iva: formData.condicionIva,
        fecha_vencimiento_certificado: formData.fechaVencimiento,
        updated_at: new Date().toISOString(),
      };

      if (certificadoContent) {
        configData.certificado_content = btoa(certificadoContent);
      }
      if (clavePrivadaContent) {
        configData.clave_privada_content = btoa(clavePrivadaContent);
      }

      let result;
      if (config) {
        result = await supabase
          .from("afip_configurations")
          .update(configData)
          .eq("doctor_id", user.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from("afip_configurations")
          .insert([configData])
          .select()
          .single();
      }

      if (result.error) {
        throw result.error;
      }

      setConfig(result.data);
      setSuccess(
        t("app.afip.saveSuccess", "Configuración de AFIP guardada exitosamente")
      );

      setFormData((prev) => ({
        ...prev,
        certificadoFile: null,
        clavePrivadaFile: null,
      }));
    } catch (error) {
      console.error("Error saving AFIP config:", error);
      setErrors({
        general: t(
          "app.afip.saveError",
          "Error al guardar la configuración. Intente nuevamente."
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const testConnection = async () => {
    if (!config) {
      setErrors({
        general: t(
          "app.afip.saveBeforeTest",
          "Debe guardar la configuración antes de probar la conexión"
        ),
      });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch("/api/afip/test-connection", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doctorId: user.id }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(t("app.afip.connectionSuccess", "Conexión con AFIP exitosa"));
      } else {
        setErrors({
          general:
            result.error ||
            t("app.afip.connectionError", "Error al conectar con AFIP"),
        });
      }
    } catch (error) {
      setErrors({
        general: t("app.afip.testConnectionError", "Error al probar la conexión"),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      <Button
        variant="outline"
        onClick={() => utils.goBack()}
        className="flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("app.afip.back", "Volver")}
      </Button>
      <div className="flex items-center gap-3">
        <Shield className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold">
            {t("app.afip.title", "Configuración AFIP")}
          </h1>
          <p className="text-gray-600">
            {t(
              "app.afip.subtitle",
              "Configure sus credenciales para facturación electrónica"
            )}
          </p>
        </div>
      </div>

      {config && (
        <div className="flex bg-green-300 border border-green-500 p-2 gap-2 items-center justify-start rounded">
          <CheckCircle className="h-4 w-4" />
          <div>
            {t("app.afip.activeSince", "Configuración activa desde")}{" "}
            {formatDateDMY(config.created_at)}
            {config.fecha_vencimiento_certificado && (
              <span className="ml-2">
                - {t("app.afip.certExpires", "Certificado vence:")}{" "}
                {formatDateDMY(config.fecha_vencimiento_certificado)}
              </span>
            )}
          </div>
        </div>
      )}

      <Tabs.Root defaultValue="config" className="w-full">
        <Tabs.List className="grid grid-cols-2 gap-1 bg-gray-100 p-2 rounded-lg mb-4">
          <Tabs.Trigger
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            value="config"
          >
            {t("app.afip.tabConfig", "Configuración")}
          </Tabs.Trigger>
          <Tabs.Trigger
            className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-black text-gray-600 text-sm font-medium p-2 rounded-md transition-colors hover:bg-gray-200"
            value="instructions"
          >
            {t("app.afip.tabInstructions", "Instructivo")}
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="config" className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {errors.general && (
              <div className="flex bg-red-300 border border-red-500 p-2 gap-2 items-center justify-start rounded">
                <AlertTriangle className="h-4 w-4" />
                <div>{errors.general}</div>
              </div>
            )}

            {success && (
              <div className="flex bg-green-300 border border-green-500 p-2 gap-2 items-center justify-start rounded">
                <CheckCircle className="h-4 w-4" />
                <div>{success}</div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    {t("app.afip.fiscalData", "Datos Fiscales")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="cuit">CUIT *</Label>
                    <Input
                      id="cuit"
                      value={formData.cuit}
                      onChange={(e) =>
                        handleInputChange(
                          "cuit",
                          e.target.value.replace(/\D/g, "").slice(0, 11)
                        )
                      }
                      placeholder="20123456789"
                      maxLength={11}
                    />
                    {errors.cuit && (
                      <p className="text-sm text-red-600 mt-1">{errors.cuit}</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="razonSocial">
                      {t("app.afip.businessName", "Razón Social *")}
                    </Label>
                    <Input
                      id="razonSocial"
                      value={formData.razonSocial}
                      onChange={(e) =>
                        handleInputChange("razonSocial", e.target.value)
                      }
                      placeholder={t(
                        "app.afip.businessNamePlaceholder",
                        "Dr. Juan Pérez"
                      )}
                    />
                    {errors.razonSocial && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.razonSocial}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="condicionIva">
                      {t("app.afip.vatCondition", "Condición IVA")}
                    </Label>
                    <Select
                      value={formData.condicionIva}
                      onValueChange={(value) =>
                        handleInputChange("condicionIva", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Responsable Inscripto">
                          {t("app.afip.registeredTaxpayer", "Responsable Inscripto")}
                        </SelectItem>
                        <SelectItem value="Monotributista">
                          {t("app.afip.monotax", "Monotributista")}
                        </SelectItem>
                        <SelectItem value="Exento">
                          {t("app.afip.exempt", "Exento")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="domicilioComercial">
                      {t("app.afip.commercialAddress", "Domicilio Comercial *")}
                    </Label>
                    <Textarea
                      id="domicilioComercial"
                      value={formData.domicilioComercial}
                      onChange={(e) =>
                        handleInputChange("domicilioComercial", e.target.value)
                      }
                      placeholder={t(
                        "app.afip.addressPlaceholder",
                        "Av. Corrientes 1234, CABA"
                      )}
                      rows={2}
                    />
                    {errors.domicilioComercial && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.domicilioComercial}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    {t("app.afip.technicalConfig", "Configuración Técnica")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="puntoVenta">
                      {t("app.afip.salesPoint", "Punto de Venta")}
                    </Label>
                    <Input
                      id="puntoVenta"
                      type="number"
                      value={formData.puntoVenta}
                      onChange={(e) =>
                        handleInputChange("puntoVenta", e.target.value)
                      }
                      min="1"
                      max="9999"
                    />
                  </div>

                  <div>
                    <Label htmlFor="ambiente">
                      {t("app.afip.environment", "Ambiente")}
                    </Label>
                    <Select
                      value={formData.ambiente}
                      onValueChange={(value) =>
                        handleInputChange("ambiente", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="testing">
                          {t("app.afip.testingEnv", "Testing (Pruebas)")}
                        </SelectItem>
                        <SelectItem value="production">
                          {t("app.afip.production", "Producción")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="fechaVencimiento">
                      {t("app.afip.certExpiry", "Vencimiento Certificado *")}
                    </Label>
                    <Input
                      id="fechaVencimiento"
                      type="date"
                      value={formData.fechaVencimiento}
                      onChange={(e) =>
                        handleInputChange("fechaVencimiento", e.target.value)
                      }
                    />
                    {errors.fechaVencimiento && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.fechaVencimiento}
                      </p>
                    )}
                  </div>

                  {formData.ambiente === "production" && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <div>
                        {t(
                          "app.afip.productionWarning",
                          "Modo producción: Las facturas serán reales y reportadas a AFIP"
                        )}
                      </div>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  {t("app.afip.certificatesTitle", "Certificados AFIP")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "app.afip.certificatesDesc",
                    "Suba sus certificados obtenidos desde AFIP. Los archivos se almacenan de forma segura y encriptada."
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="certificado">
                      {t(
                        "app.afip.certificateLabel",
                        "Certificado (.crt o .pem) *"
                      )}
                    </Label>
                    <div className="mt-1">
                      <Input
                        id="certificado"
                        type="file"
                        accept=".crt,.pem"
                        onChange={(e) =>
                          handleFileChange("certificadoFile", e.target.files[0])
                        }
                      />
                      {config?.certificado_content &&
                        !formData.certificadoFile && (
                          <Badge
                            variant="outline"
                            className="mt-2 bg-green-300"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t(
                              "app.afip.certificateConfigured",
                              "Certificado configurado"
                            )}
                          </Badge>
                        )}
                    </div>
                    {errors.certificadoFile && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.certificadoFile}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="clavePrivada">
                      {t(
                        "app.afip.privateKeyLabel",
                        "Clave Privada (.key o .pem) *"
                      )}
                    </Label>
                    <div className="mt-1">
                      <Input
                        id="clavePrivada"
                        type="file"
                        accept=".key,.pem"
                        onChange={(e) =>
                          handleFileChange(
                            "clavePrivadaFile",
                            e.target.files[0]
                          )
                        }
                      />
                      {config?.clave_privada_content &&
                        !formData.clavePrivadaFile && (
                          <Badge
                            variant="outline"
                            className="mt-2 bg-green-300"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t(
                              "app.afip.privateKeyConfigured",
                              "Clave privada configurada"
                            )}
                          </Badge>
                        )}
                    </div>
                    {errors.clavePrivadaFile && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.clavePrivadaFile}
                      </p>
                    )}
                  </div>
                </div>

                <Alert>
                  <Info className="h-4 w-4" />
                  <div>
                    {t(
                      "app.afip.certificatesStorageNote",
                      "Los certificados se almacenan encriptados en la base de datos. Solo suba archivos nuevos si necesita actualizarlos."
                    )}
                  </div>
                </Alert>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button type="submit" disabled={loading}>
                {loading
                  ? t("app.afip.saving", "Guardando...")
                  : config
                    ? t("app.afip.updateConfig", "Actualizar Configuración")
                    : t("app.afip.saveConfig", "Guardar Configuración")}
              </Button>

              {config && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={testConnection}
                  disabled={loading}
                >
                  {t("app.afip.testConnection", "Probar Conexión")}
                </Button>
              )}
            </div>
          </form>
        </Tabs.Content>

        <Tabs.Content value="instructions">
          <AfipInstructions />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

function AfipInstructions() {
  const { t } = useBrandTranslation();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t(
              "app.afip.instructionsTitle",
              "Cómo obtener certificados de AFIP"
            )}
          </CardTitle>
          <CardDescription>
            {t(
              "app.afip.instructionsDesc",
              "Siga estos pasos para obtener sus certificados de facturación electrónica"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">
                {t("app.afip.step1Title", "Paso 1: Acceder a AFIP")}
              </h3>
              <p className="text-gray-600">
                {t("app.afip.step1Intro", "Ingrese a")}{" "}
                <a
                  href="https://auth.afip.gob.ar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://auth.afip.gob.ar
                </a>{" "}
                {t("app.afip.step1WithCredentials", "con su CUIT y Clave Fiscal")}
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">
                {t(
                  "app.afip.step2Title",
                  "Paso 2: Administrador de Relaciones de Clave Fiscal"
                )}
              </h3>
              <p className="text-gray-600">
                {t(
                  "app.afip.step2Body",
                  'Vaya a "Administrador de Relaciones de Clave Fiscal" → "Certificados Digitales"'
                )}
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">
                {t("app.afip.step3Title", "Paso 3: Generar Certificado")}
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>
                  {t(
                    "app.afip.step3Item1",
                    'Seleccione "Generar nuevo certificado"'
                  )}
                </li>
                <li>
                  {t(
                    "app.afip.step3Item2",
                    'Elija "Certificado para aplicaciones"'
                  )}
                </li>
                <li>{t("app.afip.step3Item3", "Complete los datos solicitados")}</li>
                <li>{t("app.afip.step3Item4", "Descargue el archivo .p12")}</li>
              </ul>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-lg">
                {t("app.afip.step4Title", "Paso 4: Convertir certificado")}
              </h3>
              <p className="text-gray-600 mb-2">
                {t(
                  "app.afip.step4Body",
                  "Use OpenSSL para convertir el archivo .p12 a los formatos requeridos:"
                )}
              </p>
              <div className="bg-gray-100 p-3 rounded-md font-mono text-sm">
                <p>{t("app.afip.step4CommandCert", "# Extraer certificado (.crt)")}</p>
                <p>
                  openssl pkcs12 -in certificado.p12 -clcerts -nokeys -out
                  certificado.crt
                </p>
                <br />
                <p>{t("app.afip.step4CommandKey", "# Extraer clave privada (.key)")}</p>
                <p>
                  openssl pkcs12 -in certificado.p12 -nocerts -nodes -out
                  clave_privada.key
                </p>
              </div>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-lg">
                {t("app.afip.step5Title", "Paso 5: Configurar en la aplicación")}
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>{t("app.afip.step5Item1", "Suba el archivo certificado.crt")}</li>
                <li>{t("app.afip.step5Item2", "Suba el archivo clave_privada.key")}</li>
                <li>{t("app.afip.step5Item3", "Complete los datos fiscales")}</li>
                <li>{t("app.afip.step5Item4", "Pruebe la conexión")}</li>
              </ul>
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <div>
              <strong>
                {t("app.commonUi.importantInfo", "Información Importante")}:
              </strong>{" "}
              {t(
                "app.afip.keepCertsSafe",
                "Mantenga sus certificados seguros y no los comparta. La aplicación los almacena de forma encriptada para su seguridad."
              )}
            </div>
          </Alert>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">
              {t("app.afip.requiredServicesTitle", "Servicios requeridos en AFIP")}
            </h4>
            <p className="text-yellow-700 text-sm">
              {t(
                "app.afip.requiredServicesIntro",
                "Asegúrese de tener habilitados los siguientes servicios en AFIP:"
              )}
            </p>
            <ul className="list-disc list-inside text-yellow-700 text-sm mt-2 space-y-1">
              <li>
                {t(
                  "app.afip.requiredService1",
                  "Comprobantes en Línea - Factura Electrónica"
                )}
              </li>
              <li>
                {t(
                  "app.afip.requiredService2",
                  "Web Service de Facturación Electrónica"
                )}
              </li>
              <li>
                {t(
                  "app.afip.requiredService3",
                  "Autenticación y Autorización de Servicios Web"
                )}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
